import { ErrorOutline, InfoOutlined } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import TextFieldWithTooltip from '../../common/TextFieldWithTooltip/TextFieldWithTooltip';
import EmployeeSearchAutocomplete from '../../common/EmployeeSearchAutocomplete/EmployeeSearchAutocomplete';
import { useGetAllPreferences } from '../../../services/react-query/preferences';
import { useGetAllTaskTemplate } from '../../../services/react-query/task_templates/useGetAllTaskTemplate';
import { useGetEmployeeById } from '../../../services/react-query/employees/useGetEmployeeById';
import { headerTypographyProps } from '../../../styles/sxStyling';
import { Preference, PREFERENCES_VALID_KEYS } from '../../../types/Preference';
import { TaskTemplate, TaskTemplateLink } from '../../../types/TaskTemplate';
import { Nullable } from '../../../types/utils';
import { uuidFinder } from '../../../utility/inputUtils';
import { deriveInitialStartCondition, StartConditionType } from '../../../utility/startCondition';
import { DependencyOption } from '../../tasks/StartCondition';
import TaskSummary from './TaskSummary';

interface EventAddTaskModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onAdd: (linkingData: TaskTemplateLink) => void;
  taskTemplateLink?: TaskTemplateLink | null;
  dependencyOptions: DependencyOption[];
}

type ModalState = Nullable<TaskTemplateLink>;

const blankState: ModalState = {
  uuid: null,
  task_template_name: null,
  task_template_uuid: null,
  assigned_to_uuid: null,
  assigned_to_display_name: null,
  start_date: null,
  start_date_offset: null,
  depends_on_uuid: null,
  due_date_offset: null,
  task_type_uuid: null,
  task_type_value: null,
};

const triggerOptions: { label: string; value: StartConditionType }[] = [
  { label: 'No Trigger', value: 'manual' },
  { label: 'Specific Date', value: 'date' },
  { label: 'Days from Event', value: 'offset' },
  { label: 'Dependency', value: 'dependency' },
];

interface DateOffsetRowProps {
  label: string;
  prefix: string;
  tooltip: string;
  value: number | null;
  onChange: (v: number | null) => void;
}

function DateOffsetRow({ label, prefix, tooltip, value, onChange }: DateOffsetRowProps) {
  const absValue = value !== null ? Math.abs(value) : '';
  const direction: 'before' | 'after' = (value ?? 0) < 0 ? 'before' : 'after';

  return (
    <Stack spacing={0.5} sx={{ '&.MuiStack-root': { mt: 1 } }}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="body2">{label}</Typography>
        <Tooltip title={tooltip}>
          <InfoOutlined fontSize="small" color="action" />
        </Tooltip>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography variant="body2" color="text.secondary">
          {prefix}
        </Typography>
        <TextField
          type="number"
          size="small"
          sx={{ width: 80 }}
          slotProps={{ htmlInput: { min: 0 }, input: { sx: { height: 30, p: 0 } } }}
          value={absValue}
          onChange={(e) => {
            const raw = e.target.value ? Number(e.target.value) : null;
            onChange(raw !== null ? (direction === 'before' ? -raw : raw) : null);
          }}
        />
        <Typography variant="body2" color="text.secondary">
          days
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={direction}
          disabled={!value}
          onChange={(_, direction) => {
            if (!direction || !value) return;
            onChange(direction === 'before' ? -Math.abs(value) : Math.abs(value));
          }}
        >
          <ToggleButton value="before" sx={{ py: 0.5 }}>
            before
          </ToggleButton>
          <ToggleButton value="after" sx={{ py: 0.5 }}>
            after
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary">
          the event
        </Typography>
      </Stack>
    </Stack>
  );
}

export default function EventAddTaskModal({ isOpen, onCancel, onAdd, taskTemplateLink, dependencyOptions }: EventAddTaskModalProps) {
  const [modalState, setModalState] = useState<ModalState>(blankState);
  const [startCondType, setStartCondType] = useState<StartConditionType>('manual');

  const { data: taskTemplateData, isLoading: loadingTemplates } = useGetAllTaskTemplate({ params: { per_page: 0 } });
  const { data: preferenceData } = useGetAllPreferences({ params: { filters: { keys: [PREFERENCES_VALID_KEYS.TASK_TYPE] } } });
  const { data: employeeData } = useGetEmployeeById({ uuid: modalState.assigned_to_uuid || '', enabled: !!modalState.assigned_to_uuid });

  const taskTemplates = taskTemplateData?.data || [];
  const taskTypes = preferenceData || [];
  const dependencyTasks = _.reject(dependencyOptions, { uuid: modalState.uuid || '' });

  useEffect(() => {
    if (taskTemplateLink) {
      setModalState({ ...taskTemplateLink });
      setStartCondType(deriveInitialStartCondition(taskTemplateLink));
    } else {
      setModalState(blankState);
      setStartCondType('manual');
    }
  }, [isOpen, taskTemplateLink]);

  const startCondInvalid =
    (startCondType === 'dependency' && (_.isNil(modalState.depends_on_uuid) || modalState.depends_on_uuid === modalState.uuid)) ||
    (startCondType === 'date' && _.isNil(modalState.start_date)) ||
    (startCondType === 'offset' && _.isNil(modalState.start_date_offset));

  const startAfterDue =
    startCondType === 'offset' &&
    !_.isNil(modalState.start_date_offset) &&
    !_.isNil(modalState.due_date_offset) &&
    modalState.start_date_offset > modalState.due_date_offset;

  const formInvalid = !modalState.task_template_uuid || startCondInvalid || startAfterDue;

  const handleTriggerChange = (value: StartConditionType | undefined) => {
    if (!value) return;
    setStartCondType(value);
    setModalState((prev) => ({ ...prev, start_date: null, start_date_offset: null, depends_on_uuid: null }));
  };

  const handleSubmit = () => {
    if (formInvalid) return;
    onAdd({
      ...(modalState as TaskTemplateLink),
      start_date_offset: startCondType === 'offset' ? modalState.start_date_offset : null,
      due_date_offset: modalState.due_date_offset,
      uuid: taskTemplateLink?.uuid || crypto.randomUUID(),
    });
    onCancel();
  };

  return (
    <Dialog open={isOpen} onClose={onCancel} fullWidth>
      <Typography {...headerTypographyProps} sx={{ m: 2 }}>
        {taskTemplateLink ? 'Edit Task' : 'Add Task'}
      </Typography>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {/* Task Template */}
          <Autocomplete<TaskTemplate>
            size="small"
            loading={loadingTemplates}
            options={taskTemplates}
            disabled={!!taskTemplateLink}
            getOptionLabel={(t) => t.name}
            value={_.find(taskTemplates, { uuid: modalState.task_template_uuid || '' }) || null}
            onChange={(_, t) => setModalState((prev) => ({ ...prev, task_template_uuid: t?.uuid || null, task_template_name: t?.name || null }))}
            renderInput={(params) => <TextField {...params} label="Task Template" required autoFocus />}
          />

          {/* Task Type */}
          <Autocomplete<Preference>
            size="small"
            options={taskTypes}
            getOptionLabel={(p) => p.value}
            value={uuidFinder(taskTypes, modalState.task_type_uuid || '')}
            onChange={(_, p) => setModalState((prev) => ({ ...prev, task_type_uuid: p?.uuid || null, task_type_value: p?.value || null }))}
            renderInput={(params) => <TextField {...params} label="Task Type" />}
          />

          {/* Assigned To */}
          <EmployeeSearchAutocomplete
            value={employeeData || null}
            onChange={(e) =>
              setModalState((prev) => ({
                ...prev,
                assigned_to_uuid: e?.uuid || null,
                assigned_to_display_name: e?.display_name || null,
              }))
            }
            label="Assigned To"
          />

          {/* Start Trigger → Due Date */}
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 4 } }}>
            <Stack spacing={2}>
              <Autocomplete
                size="small"
                options={triggerOptions}
                getOptionLabel={(o) => o.label}
                value={_.find(triggerOptions, { value: startCondType }) || null}
                onChange={(_, o) => handleTriggerChange(o?.value)}
                renderInput={(params) => (
                  <TextFieldWithTooltip {...params} label="Start Trigger" required tooltip="When does this task become active and ready to work on?" />
                )}
              />

              {startCondType === 'date' && (
                <TextField
                  type="date"
                  size="small"
                  label="Start Date"
                  slotProps={{ inputLabel: { shrink: true } }}
                  value={modalState.start_date ?? ''}
                  onChange={(e) => setModalState((prev) => ({ ...prev, start_date: e.target.value || null }))}
                />
              )}

              {startCondType === 'offset' && (
                <DateOffsetRow
                  label="Start Date"
                  prefix="Starts"
                  tooltip="Task activates on this date and sends the assignee a reminder."
                  value={modalState.start_date_offset}
                  onChange={(v) => setModalState((prev) => ({ ...prev, start_date_offset: v }))}
                />
              )}

              {startCondType === 'dependency' && (
                <Autocomplete<DependencyOption>
                  size="small"
                  options={dependencyTasks}
                  getOptionLabel={(o) => o.name}
                  value={_.find(dependencyTasks, { uuid: modalState.depends_on_uuid || '' }) || null}
                  onChange={(_, o) => setModalState((prev) => ({ ...prev, depends_on_uuid: o?.uuid || null }))}
                  renderInput={(params) => <TextField {...params} label="Starts After" placeholder="Select a task..." />}
                />
              )}

              <DateOffsetRow
                label="Due Date"
                prefix="Due"
                tooltip="If not completed by this date, the task is marked as overdue."
                value={modalState.due_date_offset}
                onChange={(v) => setModalState((prev) => ({ ...prev, due_date_offset: v }))}
              />

              {startAfterDue && (
                <Alert icon={<ErrorOutline fontSize="small" />} severity="error" sx={{ py: 0, boxShadow: 'none' }}>
                  Due date must come after the start date.
                </Alert>
              )}
            </Stack>
          </Box>

          {/* Summary */}
          <TaskSummary modalState={modalState} startCondType={startCondType} dependencyOptions={dependencyOptions} />
        </Stack>
      </DialogContent>

      {/* Dialog Actions */}
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" disabled={formInvalid} onClick={handleSubmit}>
          {taskTemplateLink ? 'Update Task' : 'Add Task'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
